import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../services/AuthContext";
import Loading from "./Loading";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { getStoredLanguage } from "../services/language";
import {
  hasAcceptedTerms,
  markTermsAccepted,
  resolveUserTermsSubject,
} from "../constants/termsAndConditions";
import GuestRegisterPrompt from "./GuestRegisterPrompt";
import {
  buildGuestLinkEntryPath,
  getActiveGuestLinkPass,
  isRouteInGuestLinkScope,
  resolveDirectRoomLinkFromRoute,
  resolveGuestLinkFromRoute,
} from "../utils/guestLinkAccess";

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isAllowedRole = (userRole, expectedRole) => {
  if (!expectedRole) return true;

  const userRoleNormalized = normalizeRole(userRole);
  const expectedRoles = (Array.isArray(expectedRole)
    ? expectedRole
    : [expectedRole]
  ).map(normalizeRole);

  if (expectedRoles.includes(userRoleNormalized)) return true;

  const equivalents = {
    member: ["owner", "manager"],
    owner: ["member", "manager"],
    manager: ["owner", "member"],
  };
  const aliases = equivalents[userRoleNormalized] || [];
  return aliases.some((alias) => expectedRoles.includes(alias));
};

const GAME_NON_TOURNAMENT_ROUTES = new Set([
  "history",
  "lobby",
  "stats",
  "ranking",
  "learn",
  "donate",
  "store",
  "friends",
  "player",
  "spectate",
  "settings",
  "create-game",
  "play-bot",
  "play-sandbox",
  "play-local-friends",
  "design",
]);

const PREAUTH_TERMS_SUBJECT = "preauth";
const GUEST_USERNAME_PREFIX = "invitadoo";
const GUEST_EMAIL_DOMAIN = "guest.xima.local";

const isGuestSessionUser = (userValue) => {
  if (Boolean(userValue?.isGuest)) return true;
  const candidates = [
    userValue?.loginIdentifier,
    userValue?.email,
    userValue?.username,
    userValue?.sub,
  ];

  return candidates.some((candidate) => {
    const normalized = String(candidate || "").trim().toLowerCase();
    if (!normalized) return false;
    if (normalized.startsWith(GUEST_USERNAME_PREFIX)) return true;
    return normalized.endsWith(`@${GUEST_EMAIL_DOMAIN}`);
  });
};

const ProtectedRoute = ({ component: Component, role, ...rest }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [termsReady, setTermsReady] = useState(false);
  const [termsLanguage, setTermsLanguage] = useState(getStoredLanguage);
  const userAllowed = Boolean(user && isAllowedRole(user.role, role));
  const termsSubject = useMemo(() => resolveUserTermsSubject(user), [user]);
  const guestLinkPass = userAllowed ? getActiveGuestLinkPass(user) : null;
  const sharedLinkInRoute = resolveGuestLinkFromRoute(location.pathname, location.search);

  useEffect(() => {
    if (!userAllowed) {
      setTermsAccepted(true);
      setTermsReady(true);
      return;
    }

    setTermsLanguage(getStoredLanguage());
    const acceptedByUser = hasAcceptedTerms(termsSubject);
    const acceptedAsGuestPreauth =
      isGuestSessionUser(user) && hasAcceptedTerms(PREAUTH_TERMS_SUBJECT);
    const acceptedForCurrentVersion = acceptedByUser || acceptedAsGuestPreauth;

    if (acceptedAsGuestPreauth && !acceptedByUser) {
      markTermsAccepted(termsSubject);
    }

    setTermsAccepted(acceptedForCurrentVersion);
    setTermsReady(true);
  }, [termsSubject, user, userAllowed]);

  const onAcceptTerms = useCallback(() => {
    markTermsAccepted(termsSubject);
    setTermsAccepted(true);
  }, [termsSubject]);

  if (loading) {
    return <Loading />;
  }

  const linkForVisitor = user
    ? null
    : resolveDirectRoomLinkFromRoute(location.pathname, location.search);
  if (linkForVisitor) {
    // Shared puzzle / tournament link (or a copied match room address):
    // play first as a guest, register later.
    const target = `${location.pathname || ""}${location.search || ""}`;
    return <Navigate to={buildGuestLinkEntryPath(linkForVisitor, target)} replace />;
  }

  if (!userAllowed) {
    const path = String(location.pathname || "").trim();
    const query = String(location.search || "").trim();
    const hash = String(location.hash || "").trim();
    const redirectPath = `${path}${query}${hash}`;

    const queryParams = new URLSearchParams(query || "");
    const shareType = String(queryParams.get("shareType") || "")
      .trim()
      .toLowerCase();
    const isShareQueryLink = shareType === "tournament" || shareType === "independent" || shareType === "puzzle";

    const pathParts = path.split("/").filter(Boolean);
    const isDirectTournamentRoute =
      pathParts.length === 2 &&
      pathParts[0] === "game" &&
      !GAME_NON_TOURNAMENT_ROUTES.has(pathParts[1]);

    const shouldAutoGuest = isShareQueryLink || isDirectTournamentRoute;
    const loginParams = new URLSearchParams();
    if (redirectPath) {
      loginParams.set("redirect", redirectPath);
    }
    if (shouldAutoGuest) {
      loginParams.set("autoguest", "1");
    }
    const loginTarget = loginParams.toString() ? `/login?${loginParams.toString()}` : "/login";
    return <Navigate to={loginTarget} replace />;
  }

  if (guestLinkPass) {
    if (sharedLinkInRoute && sharedLinkInRoute.id !== guestLinkPass.id) {
      const target = `${location.pathname || ""}${location.search || ""}`;
      return <Navigate to={buildGuestLinkEntryPath(sharedLinkInRoute, target)} replace />;
    }
    if (!isRouteInGuestLinkScope(guestLinkPass, location)) {
      return <GuestRegisterPrompt pass={guestLinkPass} />;
    }
    // Inside the shared puzzle / match the guest plays without the terms modal;
    // the terms are accepted when they register.
    return <Component {...rest} />;
  }

  if (!termsReady) {
    return <Loading />;
  }

  if (!termsAccepted) {
    return (
      <TermsAcceptanceModal
        open
        mode="authenticated"
        loading={false}
        language={termsLanguage}
        allowCancel={false}
        onAccept={onAcceptTerms}
        onCancel={() => {}}
      />
    );
  }

  return <Component {...rest} />;
};

export default ProtectedRoute;
